import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Loader2 } from 'lucide-react';
import axios from 'axios';

import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import AdhesionsHeader from './AdhesionsHeader';
import AdhesionsTabs from './AdhesionsTabs';
import AdhesionsModals from './AdhesionsModals';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Liste des territoires
const TERRITOIRES = [
  'Guadeloupe',
  'Martinique',
  'Guyane',
  'La Réunion',
  'Mayotte',
  'Saint-Martin',
  'Saint-Barthélemy',
  'Saint-Pierre-et-Miquelon',
  'Polynésie française',
  'Nouvelle-Calédonie',
  'Wallis-et-Futuna',
  'France Métropolitaine'
];

export default function GestionAdhesions() {
  const { t } = useTranslation();
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [adhesions, setAdhesions] = useState([]);
  const [tarifs, setTarifs] = useState([]);
  const [membres, setMembres] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Filtres avancés
  const [filters, setFilters] = useState({
    source: 'all',
    territoire: 'all',
    statut: 'all',
    membreType: 'all',
    dateDebut: '',
    dateFin: '',
    recherche: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Modals
  const [openCreateAdhesion, setOpenCreateAdhesion] = useState(false);
  const [openCreateTarif, setOpenCreateTarif] = useState(false);
  const [openDetailAdhesion, setOpenDetailAdhesion] = useState(false);
  const [openValidation, setOpenValidation] = useState(false);
  const [openGenererDoc, setOpenGenererDoc] = useState(false);
  
  // Selected items
  const [selectedAdhesion, setSelectedAdhesion] = useState(null);
  
  // Form data
  const [adhesionForm, setAdhesionForm] = useState({
    membre_id: '',
    type_adhesion: 'standard',
    tarif_id: '',
    montant: 0,
    duree_mois: 12,
    notes: ''
  });
  
  const [tarifForm, setTarifForm] = useState({
    type_adhesion: 'standard',
    nom: '',
    description: '',
    montant_annuel: 0,
    montant_mensuel: 0,
    avantages: [],
    actif: true
  });
  
  const [validationData, setValidationData] = useState({
    action: 'valider',
    raison: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      
      const [statsRes, adhesionsRes, tarifsRes, membresRes] = await Promise.all([
        axios.get(`${API}/admin/adhesions/statistiques/global`, { headers }),
        axios.get(`${API}/admin/all-adhesions`, { headers }),
        axios.get(`${API}/admin/tarifs-adhesion`, { headers }),
        axios.get(`${API}/users`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setAdhesions(adhesionsRes.data.adhesions || []);
      setTarifs(tarifsRes.data.tarifs || []);
      
      // Filtrer uniquement les membres avec role='member' et trier du plus récent au plus ancien
      const allUsers = membresRes.data;
      const membersOnly = Array.isArray(allUsers) 
        ? allUsers.filter(u => u.role === 'member')
            .sort((a, b) => new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0))
        : [];
      setMembres(membersOnly);
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Filtrage des adhésions avec useMemo pour performance
  const filteredAdhesions = useMemo(() => {
    return adhesions.filter(adhesion => {
      // Filtre par source
      if (filters.source !== 'all' && adhesion.source !== filters.source) {
        return false;
      }

      // Filtre par type de membre (société/particulier)
      if (filters.membreType !== 'all' && adhesion.membre_type !== filters.membreType) {
        return false;
      }
      
      // Filtre par territoire
      if (filters.territoire !== 'all') {
        const territoire = adhesion.territoire || adhesion.territory || '';
        if (!territoire.toLowerCase().includes(filters.territoire.toLowerCase())) {
          return false;
        }
      }
      
      // Filtre par statut
      if (filters.statut !== 'all') {
        const statut = adhesion.statut || '';
        if (filters.statut === 'active' && !['active', 'validee', 'completed', 'paid'].includes(statut)) {
          return false;
        }
        if (filters.statut === 'pending' && !['en_attente', 'pending', 'en_cours', 'created'].includes(statut)) {
          return false;
        }
        if (filters.statut === 'expired' && !['expiree', 'expired', 'annulee'].includes(statut)) {
          return false;
        }
      }
      
      // Filtre par date de début
      if (filters.dateDebut) {
        const dateAdhesion = new Date(adhesion.created_at || adhesion.date_creation);
        const dateDebut = new Date(filters.dateDebut);
        if (dateAdhesion < dateDebut) {
          return false;
        }
      }
      
      // Filtre par date de fin
      if (filters.dateFin) {
        const dateAdhesion = new Date(adhesion.created_at || adhesion.date_creation);
        const dateFin = new Date(filters.dateFin);
        dateFin.setHours(23, 59, 59, 999);
        if (dateAdhesion > dateFin) {
          return false;
        }
      }
      
      // Filtre par recherche
      if (filters.recherche) {
        const search = filters.recherche.toLowerCase();
        const nom = (adhesion.membre_nom || '').toLowerCase();
        const email = (adhesion.membre_email || '').toLowerCase();
        const numero = (adhesion.numero_adhesion || adhesion.id || '').toLowerCase();
        if (!nom.includes(search) && !email.includes(search) && !numero.includes(search)) {
          return false;
        }
      }
      
      return true;
    });
  }, [adhesions, filters]);

  // Réinitialiser les filtres
  const resetFilters = () => {
    setFilters({
      source: 'all',
      territoire: 'all',
      statut: 'all',
      membreType: 'all',
      dateDebut: '',
      dateFin: '',
      recherche: ''
    });
  };

  // Export CSV
  const exportCSV = () => {
    setExporting(true);
    try {
      const headers = ['Nom', 'Email', 'Type', 'Statut', 'Montant', 'Territoire', 'Date création'];
      const rows = filteredAdhesions.map(a => [
        a.membre_nom || '',
        a.membre_email || '',
        a.type_source || a.type_adhesion || '',
        a.statut || '',
        a.montant || 0,
        a.territoire || '',
        a.created_at ? new Date(a.created_at).toLocaleDateString('fr-FR') : ''
      ]);
      
      const csvContent = [
        headers.join(';'),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
      ].join('\n');
      
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `adhesions_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`✅ ${filteredAdhesions.length} adhésions exportées en CSV`);
    } catch {
      toast.error('Erreur lors de l\'export CSV');
    } finally {
      setExporting(false);
    }
  };

  // Export PDF
  const exportPDF = async () => {
    setExporting(true);
    try {
      // Créer le contenu HTML pour le PDF
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Export Adhésions</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #4F46E5; text-align: center; }
            .stats { display: flex; justify-content: space-around; margin: 20px 0; padding: 15px; background: #f3f4f6; border-radius: 8px; }
            .stat { text-align: center; }
            .stat-value { font-size: 24px; font-weight: bold; color: #4F46E5; }
            .stat-label { font-size: 12px; color: #6b7280; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #4F46E5; color: white; padding: 10px; text-align: left; }
            td { padding: 8px; border-bottom: 1px solid #e5e7eb; }
            tr:nth-child(even) { background: #f9fafb; }
            .badge { padding: 2px 8px; border-radius: 4px; font-size: 11px; }
            .badge-green { background: #d1fae5; color: #065f46; }
            .badge-orange { background: #fed7aa; color: #9a3412; }
            .badge-red { background: #fecaca; color: #991b1b; }
            .badge-blue { background: #dbeafe; color: #1e40af; }
            .badge-amber { background: #fef3c7; color: #92400e; }
            .footer { text-align: center; margin-top: 30px; font-size: 11px; color: #9ca3af; }
          </style>
        </head>
        <body>
          <h1>📋 Export des Adhésions</h1>
          <p style="text-align: center; color: #6b7280;">Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}</p>
          
          <div class="stats">
            <div class="stat">
              <div class="stat-value">${filteredAdhesions.length}</div>
              <div class="stat-label">Total</div>
            </div>
            <div class="stat">
              <div class="stat-value">${filteredAdhesions.filter(a => ['active', 'validee', 'completed', 'paid'].includes(a.statut)).length}</div>
              <div class="stat-label">Actives</div>
            </div>
            <div class="stat">
              <div class="stat-value">${filteredAdhesions.filter(a => a.source === 'ftpe').length}</div>
              <div class="stat-label">FTPE</div>
            </div>
            <div class="stat">
              <div class="stat-value">${filteredAdhesions.filter(a => a.source === 'rcd').length}</div>
              <div class="stat-label">RCD</div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Membre</th>
                <th>Type</th>
                <th>Statut</th>
                <th>Montant</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              ${filteredAdhesions.slice(0, 100).map(a => `
                <tr>
                  <td>
                    <strong>${a.membre_nom || 'N/A'}</strong><br>
                    <span style="font-size: 11px; color: #6b7280;">${a.membre_email || ''}</span>
                  </td>
                  <td>
                    <span class="badge ${a.source === 'ftpe' ? 'badge-blue' : a.source === 'rcd' ? 'badge-green' : 'badge-green'}">
                      ${a.type_source || a.type_adhesion || 'N/A'}
                    </span>
                  </td>
                  <td>
                    <span class="badge ${['active', 'validee', 'completed', 'paid'].includes(a.statut) ? 'badge-green' : ['en_attente', 'pending', 'en_cours'].includes(a.statut) ? 'badge-orange' : 'badge-red'}">
                      ${a.statut || 'N/A'}
                    </span>
                  </td>
                  <td><strong>${a.montant || 0} €</strong></td>
                  <td>${a.created_at ? new Date(a.created_at).toLocaleDateString('fr-FR') : 'N/A'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          ${filteredAdhesions.length > 100 ? `<p style="text-align: center; color: #9ca3af; margin-top: 10px;">... et ${filteredAdhesions.length - 100} autres adhésions</p>` : ''}
          
          <div class="footer">
            Document généré automatiquement par le système de gestion des adhésions
          </div>
        </body>
        </html>
      `;
      
      // Ouvrir dans une nouvelle fenêtre pour impression
      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      
      // Déclencher l'impression après un court délai
      setTimeout(() => {
        printWindow.print();
      }, 500);
      
      toast.success(`✅ ${filteredAdhesions.length} adhésions exportées en PDF`);
    } catch {
      toast.error('Erreur lors de l\'export PDF');
    } finally {
      setExporting(false);
    }
  };
  
  const handleCreateAdhesion = async () => {
    if (!adhesionForm.membre_id) {
      toast.error('Veuillez sélectionner un membre');
      return;
    }
    
    if (!adhesionForm.montant || adhesionForm.montant <= 0) {
      toast.error('Veuillez saisir un montant valide');
      return;
    }
    
    try {
      const headers = getAuthHeader();
      await axios.post(`${API}/admin/adhesions`, adhesionForm, { headers });
      toast.success('✅ Adhésion créée avec succès');
      setOpenCreateAdhesion(false);
      setAdhesionForm({
        membre_id: '',
        type_adhesion: 'standard',
        tarif_id: '',
        montant: 0,
        duree_mois: 12,
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };
  
  const handleCreateTarif = async () => {
    if (!tarifForm.nom || !tarifForm.montant_annuel) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    
    try {
      const headers = getAuthHeader();
      await axios.post(`${API}/admin/tarifs-adhesion`, tarifForm, { headers });
      toast.success('✅ Tarif créé avec succès');
      setOpenCreateTarif(false);
      setTarifForm({
        type_adhesion: 'standard',
        nom: '',
        description: '',
        montant_annuel: 0,
        montant_mensuel: 0,
        avantages: [],
        actif: true
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };
  
  const handleRenouveler = async (adhesionOrId, source) => {
    // Compat ascendante: l'appelant peut passer soit l'objet adhésion entier,
    // soit l'ID. On extrait l'ID et la source pour router la requête côté backend.
    const isObj = adhesionOrId && typeof adhesionOrId === 'object';
    const adhesionId = isObj ? adhesionOrId.id : adhesionOrId;
    const srcParam = source || (isObj ? adhesionOrId.source : undefined);
    try {
      const headers = getAuthHeader();
      const url = srcParam
        ? `${API}/admin/adhesions/${adhesionId}/renouveler?source=${encodeURIComponent(srcParam)}`
        : `${API}/admin/adhesions/${adhesionId}/renouveler`;
      await axios.post(url, {}, { headers });
      toast.success('✅ Adhésion renouvelée avec succès');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du renouvellement');
    }
  };
  
  const handleValiderRejeter = async () => {
    if (!selectedAdhesion) return;
    
    if (validationData.action === 'rejeter' && !validationData.raison) {
      toast.error('Veuillez indiquer la raison du rejet');
      return;
    }
    
    try {
      const headers = getAuthHeader();
      await axios.post(
        `${API}/admin/adhesions/${selectedAdhesion.id}/valider`,
        validationData,
        { headers }
      );
      
      toast.success(validationData.action === 'valider' 
        ? '✅ Adhésion validée' 
        : '❌ Adhésion rejetée'
      );
      
      setOpenValidation(false);
      setValidationData({ action: 'valider', raison: '' });
      setSelectedAdhesion(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };
  
  const handleGenererDocument = async (type) => {
    if (!selectedAdhesion) return;
    
    try {
      const headers = getAuthHeader();
      const _response = await axios.post(
        `${API}/admin/adhesions/${selectedAdhesion.id}/generer-document`,
        null,
        { 
          headers,
          params: { type_document: type }
        }
      );
      
      toast.success(`✅ Document ${type} généré avec succès`);
      setOpenGenererDoc(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  const ctx = { t, stats, adhesions, tarifs, membres, activeTab, setActiveTab, filters, setFilters, showFilters, setShowFilters, exporting, openCreateAdhesion, setOpenCreateAdhesion, openCreateTarif, setOpenCreateTarif, openDetailAdhesion, setOpenDetailAdhesion, openValidation, setOpenValidation, openGenererDoc, setOpenGenererDoc, selectedAdhesion, setSelectedAdhesion, adhesionForm, setAdhesionForm, tarifForm, setTarifForm, validationData, setValidationData, filteredAdhesions, resetFilters, exportCSV, exportPDF, handleCreateAdhesion, handleCreateTarif, handleRenouveler, handleValiderRejeter, handleGenererDocument, TERRITOIRES };

  return (
    <div className="space-y-6">
      <AdhesionsHeader ctx={ctx} />
      <AdhesionsTabs ctx={ctx} />
      <AdhesionsModals ctx={ctx} />
    </div>
  );
}
